import React from 'react';
import { ArrowDownLeft, ArrowUpRight, Scale, Sparkles } from 'lucide-react';
import { money, monthTx, totals, MONTHS } from './financeUtils';

const plural = (n, s, p) => `${n} ${n === 1 ? s : p}`;

const Stat = ({ label, icon: Icon, value, cls, foot, testId, wide }) => (
  <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-4 ${wide ? 'col-span-2' : ''}`} data-testid={testId}>
    <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
      <span>{label}</span>
      <span className="w-7 h-7 rounded-lg bg-slate-50 flex items-center justify-center text-emerald-700"><Icon className="w-4 h-4" /></span>
    </div>
    <p className={`text-xl font-bold tracking-tight mt-2 ${cls}`}>{value}</p>
    <p className="text-[11px] text-slate-400">{foot}</p>
  </div>
);

export const SummaryCards = ({ tx, view }) => {
  const a = totals(tx, view);
  const bal = a.income - a.expense;
  const list = monthTx(tx, view);
  const ni = list.filter((t) => t.type === 'income').length;
  const ne = list.length - ni;
  return (
    <section className="grid grid-cols-2 gap-3" aria-label="Resumo do mês">
      <Stat label="Entradas" icon={ArrowDownLeft} value={money(a.income)} cls="text-emerald-700" foot={`${plural(ni, 'entrada registrada', 'entradas registradas')}`} testId="finance-income" />
      <Stat label="Saídas" icon={ArrowUpRight} value={money(a.expense)} cls="text-red-600" foot={`${plural(ne, 'saída registrada', 'saídas registradas')}`} testId="finance-expense" />
      <Stat label="Saldo do mês" icon={Scale} value={money(bal)} cls={bal < 0 ? 'text-red-600' : 'text-emerald-700'} foot={bal >= 0 ? 'Entradas menos saídas' : 'As saídas superaram as entradas'} testId="finance-balance" wide />
    </section>
  );
};

export const EvolutionChart = ({ tx, view }) => {
  const arr = Array.from({ length: 6 }, (_, i) => new Date(view.getFullYear(), view.getMonth() - 5 + i, 1));
  const vals = arr.map((d) => ({ ...totals(tx, d), d }));
  const max = Math.max(1, ...vals.flatMap((x) => [x.income, x.expense]));
  const cur = totals(tx, view);
  const prev = totals(tx, new Date(view.getFullYear(), view.getMonth() - 1, 1));
  let msg = 'Adicione suas movimentações para acompanhar sua evolução financeira por aqui.';
  if (cur.income || cur.expense) {
    if (cur.income > cur.expense) msg = `Bom trabalho! Neste mês você ficou com ${money(cur.income - cur.expense)} após as despesas.`;
    else if (cur.expense > cur.income) msg = `Fique de olho: suas saídas superam as entradas em ${money(cur.expense - cur.income)}.`;
    else msg = 'Entradas e saídas estão equilibradas neste mês.';
  } else if (prev.income || prev.expense) msg = 'Ainda não há movimentações neste mês. Veja os meses anteriores no gráfico.';
  const pct = cur.income ? Math.min(100, (cur.expense / cur.income) * 100) : 0;
  return (
    <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" id="evolucao" data-testid="finance-chart">
      <h3 className="font-semibold text-slate-900 text-sm">Evolução mês a mês</h3>
      <p className="text-[11px] text-slate-500 mb-3">Entradas e saídas nos últimos 6 meses</p>
      <div className="h-36 flex items-end gap-2 border-b border-slate-100">
        {vals.map((x, i) => {
          const isCur = i === 5;
          return (
            <div key={i} className="flex-1 h-full flex flex-col justify-end items-center gap-1">
              <div className="h-28 w-full flex items-end justify-center gap-1">
                <div title={`Entradas ${money(x.income)}`} className={`w-3 rounded-t ${isCur ? 'bg-emerald-600' : 'bg-emerald-200'}`} style={{ height: `${Math.max(x.income ? 4 : 0, (x.income / max) * 100)}%` }} />
                <div title={`Saídas ${money(x.expense)}`} className={`w-3 rounded-t ${isCur ? 'bg-red-400' : 'bg-red-200'}`} style={{ height: `${Math.max(x.expense ? 4 : 0, (x.expense / max) * 100)}%` }} />
              </div>
              <span className="text-[10px] text-slate-400 pb-1">{MONTHS[x.d.getMonth()].slice(0, 3)}</span>
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-xs text-slate-600 bg-emerald-50/60 rounded-xl p-3 flex gap-2" data-testid="finance-insight"><Sparkles className="w-4 h-4 text-emerald-600 shrink-0" />{msg}</p>
      <div className="flex justify-between text-[11px] text-slate-500 mt-3"><span>Renda comprometida</span><strong data-testid="finance-commit">{Math.round(pct)}%</strong></div>
      <div className="h-2 bg-slate-100 rounded-full mt-1 overflow-hidden"><div className="h-full bg-amber-500 rounded-full transition-[width]" style={{ width: `${pct}%` }} /></div>
    </section>
  );
};
